import React, { ReactNode, useRef, useState } from "react";
import SwiperClass from "swiper";
import { Navigation, Scrollbar } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Box, BoxProps } from "../Box/Box";
import { ChevronLeft } from "../Icons/ChevronLeft";
import { ChevronRight } from "../Icons/ChevronRight";
import useMediaQuery, { breakpoints } from "@UI/hooks/useMediaQuery";

// Swiper normally relies on swiper/css for its flex layout.
// Since the webpack config cannot process that CSS file, we inject
// the minimal required styles directly on the wrapper element.
// Swiper normally relies on swiper/css for its flex layout.
// Since the webpack config cannot process that CSS file, we inject
// the minimal required styles directly on the wrapper element.
const SWIPER_STYLES = `
  .rt-carousel .swiper-wrapper { display: flex; flex-direction: row; align-items: stretch; }
  .rt-carousel .swiper-slide { flex-shrink: 0; height: auto; display: flex; flex-direction: column; }
  .rt-carousel .swiper-slide > * { flex: 1; }
  .rt-carousel .swiper-scrollbar { position: relative; margin-top: 8px; height: 4px; background: rgba(0,0,0,0.1); border-radius: 4px; }
  .rt-carousel .swiper-scrollbar-drag { height: 100%; background: rgba(0,0,0,0.3); border-radius: 4px; cursor: pointer; }
`;

const NAV_BUTTON_SX = (dir: "left" | "right") => ({
  transition: "opacity 0.3s ease",
  "&.swiper-button-disabled": { opacity: 0, pointerEvents: "none" },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `linear-gradient(to ${dir}, white 20%, transparent)`,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover::before": { opacity: 1 },
  "&:hover svg": { transform: "scale(1.1)" },
  "&:active": { opacity: 0.6 },
  "& svg": {
    transition: "transform 0.15s ease",
    position: "relative",
    zIndex: 1,
  },
});

const NavButton = React.forwardRef<
  HTMLDivElement,
  { direction: "prev" | "next"; hidden: boolean }
>(({ direction, hidden }, ref) => {
  const isPrev = direction === "prev";
  const gradientDir = isPrev ? "right" : "left";
  return (
    <Box
      ref={ref}
      position="absolute"
      top={0}
      bottom={0}
      {...(isPrev ? { left: -2 } : { right: -2 })}
      width="2.5rem"
      display={hidden && isPrev ? "none" : "flex"}
      alignItems="center"
      justifyContent={isPrev ? "flex-start" : "flex-end"}
      cursor={hidden ? "default" : "pointer"}
      zIndex={1}
      pointerEvents={hidden ? "none" : undefined}
      background={`linear-gradient(to ${gradientDir}, ${hidden ? "rgba(255,255,255,0.5)" : "white"}, transparent)`}
      sx={NAV_BUTTON_SX(gradientDir)}
      onMouseDown={(e) => e.preventDefault()}
    >
      {!hidden && (isPrev ? (
        <ChevronLeft style={{ width: "1.2rem" }} />
      ) : (
        <ChevronRight style={{ width: "1.2rem" }} />
      ))}
    </Box>
  );
});
NavButton.displayName = "NavButton";

export interface CarouselProps extends Omit<BoxProps, "css"> {
  children?: ReactNode;
  /** Content rendered on the left side of the header row (above the carousel). */
  header?: ReactNode;
  /** Gap between slides in px when not on mobile. Defaults to 12. On mobile the gap is always 8px. */
  spaceBetween?: number;
  /**
   * Fixed width for each slide (e.g. "300px"). When provided, uses slidesPerView="auto".
   * When omitted, falls back to responsive breakpoint-based slidesPerView.
   */
  slideWidth?: string;
  /** Callback fired when the active slide changes. */
  onSlideChange?: (swiper: SwiperClass) => void;
}

export const Carousel = ({
  children,
  header,
  spaceBetween = 12,
  slideWidth,
  onSlideChange,
  ...boxProps
}: CarouselProps) => {
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);
  const [swiperReady, setSwiperReady] = useState(false);
  const { isMobile } = useMediaQuery();

  return (
    <Box {...boxProps}>
      {header && <Box mb={4}>{header}</Box>}

      <Box position="relative">
        <style>{SWIPER_STYLES}</style>

        <Swiper
          className="rt-carousel"
          modules={[Navigation, Scrollbar]}
          slidesPerView={slideWidth ? "auto" : 1.5}
          spaceBetween={8}
          scrollbar={{ draggable: true }}
          breakpoints={{
            [breakpoints.md]: {
              ...(slideWidth ? {} : { slidesPerView: 2.5, spaceBetween }),
              scrollbar: { hide: true },
            },
            ...(slideWidth
              ? {}
              : {
                  [breakpoints.lg]: { slidesPerView: 3.5, spaceBetween },
                  [breakpoints.xl]: { slidesPerView: 4.5, spaceBetween },
                }),
          }}
          grabCursor
          navigation={
            swiperReady
              ? { prevEl: prevRef.current, nextEl: nextRef.current }
              : false
          }
          onSwiper={() => setSwiperReady(true)}
          onSlideChange={(s: SwiperClass) => onSlideChange?.(s)}
          style={{ paddingBottom: "1rem" }}
        >
          {React.Children.map(children, (child, i) => {
            if (!child) return null;
            return (
              <SwiperSlide
                key={i}
                style={
                  slideWidth
                    ? { width: slideWidth, maxWidth: "95%" }
                    : undefined
                }
              >
                {child}
              </SwiperSlide>
            );
          })}
        </Swiper>

        <NavButton ref={prevRef} direction="prev" hidden={isMobile} />
        <NavButton ref={nextRef} direction="next" hidden={isMobile} />
      </Box>
    </Box>
  );
};
